import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, tokenStore } from '@/lib/api';

const AuthContext = createContext(null);
const COMPANY_KEY = 'pillot_company_id';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState(null); // full detail incl. subscription + modules
  const [loading, setLoading] = useState(true);

  const loadCompany = useCallback(async (companyId) => {
    if (!companyId) {
      setCompany(null);
      return null;
    }
    try {
      const { data } = await api.get(`/companies/${companyId}`);
      setCompany(data);
      localStorage.setItem(COMPANY_KEY, companyId);
      return data;
    } catch (e) {
      localStorage.removeItem(COMPANY_KEY);
      setCompany(null);
      return null;
    }
  }, []);

  const applySession = useCallback(
    async (payload) => {
      setUser(payload.user);
      setCompanies(payload.companies || []);
      const stored = localStorage.getItem(COMPANY_KEY);
      const list = payload.companies || [];
      const pickId = list.find((c) => c.id === stored)?.id || (payload.user?.is_super_admin && stored) || list[0]?.id || null;
      const data = await loadCompany(pickId);
      if (data && !list.some((c) => c.id === data.id)) setCompanies([...list, { ...data, role: 'super_admin' }]);
      else if (!data && pickId && pickId !== list[0]?.id) await loadCompany(list[0]?.id || null);
    },
    [loadCompany],
  );

  const bootstrap = useCallback(async () => {
    if (!tokenStore.access) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      await applySession(data);
    } catch (e) {
      tokenStore.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [applySession]);

  useEffect(() => {
    bootstrap();
    const onLogout = () => {
      setUser(null);
      setCompanies([]);
      setCompany(null);
    };
    window.addEventListener('pillot:logout', onLogout);
    return () => window.removeEventListener('pillot:logout', onLogout);
  }, [bootstrap]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    tokenStore.set(data.access_token, data.refresh_token);
    await applySession(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    tokenStore.set(data.access_token, data.refresh_token);
    await applySession(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      /* ignore */
    }
    tokenStore.clear();
    localStorage.removeItem(COMPANY_KEY);
    setUser(null);
    setCompanies([]);
    setCompany(null);
  };

  const refreshMe = async () => {
    const { data } = await api.get('/auth/me');
    setUser(data.user);
    setCompanies(data.companies || []);
    return data;
  };

  const switchCompany = async (id) => {
    const data = await loadCompany(id);
    if (data) setCompanies((list) => (list.some((c) => c.id === data.id) ? list : [...list, { ...data, role: 'super_admin' }]));
    return data;
  };
  const refreshCompany = async () => (company ? loadCompany(company.id) : null);

  return (
    <AuthContext.Provider value={{ user, companies, company, loading, login, register, logout, refreshMe, switchCompany, refreshCompany, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
